import { useTranslation } from "react-i18next";
import { Badge, ChipList } from "@h2j-data-team/ui";
import type { QvdAnalysisScope } from "@repo/shared";

const DEFAULT_LIMIT = 3;

/**
 * Renders an analysis run's scope: a "Whole tenant" badge, or the selected
 * spaces and name patterns as chips limited to `limit`, the rest behind a "+N"
 * chip that expands the list in place. Runs created before space names were
 * persisted fall back to a count badge.
 */
export function QvdScopeBadges({
  scope,
  limit = DEFAULT_LIMIT,
}: {
  scope: QvdAnalysisScope;
  limit?: number;
}) {
  const { t } = useTranslation("qvd-analysis");

  // `?? []` — runs from cached/list payloads predate name patterns.
  const patterns = scope.namePatterns ?? [];
  const hasSelection = scope.spaceIds.length > 0 || scope.qvdIds.length > 0;

  // A pattern-filtered tenant-wide run is not the whole tenant.
  if (!hasSelection && patterns.length === 0) {
    return <Badge variant="secondary">{t("scope.wholeTenant")}</Badge>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {scope.spaces.length > 0 ? (
        <ChipList
          aria-label={t("scope.spacesLabel")}
          items={scope.spaces.map((s) => ({
            id: s.spaceId,
            label: s.spaceName,
            hint: s.spaceName,
          }))}
          max={limit}
          overflowLabel={(count) => t("scope.moreSpaces", { count })}
          collapseLabel={t("scope.showLess")}
        />
      ) : (
        hasSelection && (
          // Older run without persisted names — show the space count.
          <Badge variant="secondary">
            {t("scope.spacesCount", { count: scope.spaceIds.length })}
          </Badge>
        )
      )}
      {patterns.length > 0 && (
        // Name patterns get the outline + mono treatment so they don't read as spaces.
        <ChipList
          className="[&>li>*]:font-mono"
          aria-label={t("scope.patternsLabel")}
          items={patterns.map((pattern, i) => ({
            id: `${i}:${pattern}`,
            label: pattern,
            hint: pattern,
            variant: "outline",
          }))}
          max={limit}
          overflowLabel={(count) => t("scope.morePatterns", { count })}
          collapseLabel={t("scope.showLess")}
        />
      )}
    </div>
  );
}
